"use client";

import { useEffect } from "react";
import type { GlobalSearchOutput } from "../../schema";
import type {
  GlobalSearchEmitFunction,
  GuidepostEntry,
  SearchEntry,
  SearchSection,
  UniversalSearchResult,
} from "../types";
import { GlobalSearchEvent } from "../types";
import { addSearchHistory } from "./searchHistory";
import { useAssetSearch } from "./useAssetSearch";
import { useDebouncedQuery } from "./useDebouncedQuery";
import { useRecentlyUpdated } from "./useRecentlyUpdated";
import { useUniversalSearch } from "./useUniversalSearch";

interface UseSearchDropdownDataOptions {
  query: string;
  emit?: GlobalSearchEmitFunction;
  includeNextGenZaps?: boolean;
  /** Arms the recently-updated request once the dropdown has been opened. */
  initiallyEnabled?: boolean;
}

export interface SearchDropdownData {
  debouncedQuery: string;
  searchResults: UniversalSearchResult | undefined;
  guidepost: GuidepostEntry | undefined;
  searchSections: SearchSection[];
  indexesWithResults: SearchSection[];
  loadingPorter: boolean;
  porterResolvedQuery: string;
  assetData: GlobalSearchOutput | undefined;
  loadingAssets: boolean;
  assetError?: unknown;
  retryAssets: () => void;
  recentlyUpdated: SearchEntry[];
  loadingRecentlyUpdated: boolean;
}

/**
 * Everything the search dropdown reads: the debounced query, the two search
 * requests, the recently-updated list, and the analytics/history side effects
 * that follow a resolved query.
 *
 * This deliberately lives above the popover. The popover unmounts its subtree
 * on close, so a hook called from inside it would refetch and re-emit on every
 * reopen — the user would watch the skeleton again and analytics would count
 * one query per open rather than per search.
 */
export function useSearchDropdownData({
  query,
  emit,
  includeNextGenZaps,
  initiallyEnabled = true,
}: UseSearchDropdownDataOptions): SearchDropdownData {
  const { debouncedQuery } = useDebouncedQuery(query);
  const porter = useUniversalSearch(debouncedQuery);
  const assets = useAssetSearch(debouncedQuery, undefined, includeNextGenZaps);
  const recentlyUpdated = useRecentlyUpdated({ initiallyEnabled });

  useEffect(() => {
    if (debouncedQuery && porter.searchResults) {
      emit?.({
        event: GlobalSearchEvent.SearchResultsReturned,
        eventAction: "view",
        text: debouncedQuery,
      });
    }
  }, [porter.searchResults, debouncedQuery, emit]);

  useEffect(() => {
    if (debouncedQuery) {
      addSearchHistory(debouncedQuery);
      emit?.({
        event: GlobalSearchEvent.Query,
        eventAction: "edit",
        text: debouncedQuery,
      });
    }
  }, [debouncedQuery, emit]);

  return {
    debouncedQuery,
    searchResults: porter.searchResults,
    guidepost: porter.guidepost,
    searchSections: porter.searchSections,
    indexesWithResults: porter.indexesWithResults,
    loadingPorter: porter.loading,
    porterResolvedQuery: porter.resolvedQuery,
    assetData: assets.data,
    loadingAssets: assets.loading,
    assetError: assets.error,
    retryAssets: assets.retry,
    recentlyUpdated: recentlyUpdated.items,
    loadingRecentlyUpdated: recentlyUpdated.loading,
  };
}
